import { Theme, css } from '@emotion/react';
import { media, responsiveMaxWidth } from '@styles/css';

import type { GetServerSidePropsContext, NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { Suspense, SyntheticEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useChatCharacterQuery } from '@hooks/query/useChatCharacterQuery';
import { useSituationQuery } from '@hooks/query/useSituationQuery';
import {
  BROADCAST_CHANNEL,
  useBroadcastChannel,
} from '@hooks/useBroadcastChannel';
import { useFirebase } from '@hooks/useFirebase';
import { useModal } from '@hooks/useModal';
import useToast from '@hooks/useToast';
import { Sizes, Spacing } from 'chatie-design-system';
import { Icon, VerticalEllipsis } from 'chatie-design-system/icon';

import CharacterDetailInfoArea from '@components/characters/CharacterDetailInfoArea';
import CharacterProfile from '@components/characters/CharacterProfile';
import SituationList from '@components/characters/SituationList';
import CharacterChemiInfoArea from '@components/cheming/CharacterChemiInfoArea';
import CharacterDetailInfo from '@components/cheming/CharacterDetailInfo';
import ActionSheet, { ActionSheetOption } from '@components/common/ActionSheet';
import Button from '@components/common/Button';
import { ErrorFallback } from '@components/common/Error';
import Loading from '@components/common/Loading';
import Tab, { useTab } from '@components/common/Tab';
import CharacterCommunityList from '@components/community/CharacterCommunityList';
import Header from '@components/layout/Header';
import Layout from '@components/layout/Layout';
import Push, { PushSize } from '@components/layout/Push';
import SafeArea from '@components/layout/SafeArea';
import CharacterDeleteModal from '@components/modal/CharacterDeleteModal';
import ChemiStepBottomModal from '@components/modal/ChemiStepBottomModal';
import CharacterDetailSkeleton from '@components/skeleton/CharacterDetailSkeleton';

import {
  CHAT_BOT_API,
  ChatRoomStatus,
  ChatRoomsQueryType,
  deletePlaySet,
  fetchChatRooms,
  getAIChatSituationDetail,
} from '@api/chatbot';
import { fetchCharacterPosts } from '@api/community';
import { postCaptureUUID } from '@api/event';
import { DOMAIN, Platform, QUERY_KEYS, ReportObjectType } from '@constants';
import { withAuth } from '@contexts/auth';
import { useGlobalContext } from '@contexts/global';
import { IChatCharacter, IChatResponse } from '@model';
import { useAnalytics } from '@utils/analytics';
import { addDuration, isBefore } from '@utils/date';
import { useMe, useScreen } from '@utils/hook';
import { getFlow, setFlow, toQueryString } from '@utils/misc';
import { getRouterQuery, usePaginationQuery } from '@utils/query';
import {
  combineProps,
  withDehydrate,
  withGetServerSideProps,
} from '@utils/ssr';
import { useWebView } from '@utils/webview';

const floatingButtonContainerCSS = (theme: Theme) =>
  css`
    display: flex;
    justify-content: center;
    width: 100%;
    ${responsiveMaxWidth};
    height: calc(env(safe-area-inset-bottom) + ${Spacing.large + 40}px);

    .floating-button-wrap {
      position: fixed;
      display: flex;
      max-width: inherit;
      width: 100%;
      gap: 10px;
      padding: 10px 16px 20px;
      border-top: 1px solid ${theme.var.gray100};
      z-index: 50;
      bottom: 0px;
      background-color: ${theme.var.white};

      ${media.tablet} {
        padding: 10px ${Spacing.xlarge}px 20px;
      }
      ${media.desktop} {
        padding: 10px ${Spacing.xxlarge}px 20px;
      }

      .btn {
        height: 44px;
      }
    }
  `;

type CharacterDetailAction = 'modify' | 'delete' | 'report';
type DefaultCharacterTab = 'community' | 'profile';
type OfficialCharacterTab = 'situation' | 'community';
type ChemingCharacterTab = 'character' | 'profile';

export interface CharacterDetailProps {
  id?: number;
  isModal?: boolean;
}

export const CharacterDetail = ({ id, isModal }: CharacterDetailProps) => {
  const router = useRouter();
  const shareUUID = router.query.share_uid as string;
  const queryClient = useQueryClient();
  const characterId = id
    ? id
    : (getRouterQuery(router.query.characterId) as number);

  const { platform, isCheming } = useGlobalContext();
  const { firebase } = useAnalytics();
  const { web, closePageSafe } = useWebView();
  const { showToast } = useToast();
  const { inflow } = useFirebase();
  const { t } = useTranslation();

  const { data: me } = useMe();
  const { mutate: postUUID } = useMutation(postCaptureUUID);
  const { useGetCharacterDetail } = useChatCharacterQuery();
  const { data: character } = useGetCharacterDetail(characterId);

  const { useGetSituationsByCharacterGroup } = useSituationQuery();
  const query = useGetSituationsByCharacterGroup(characterId, 'desc');
  const characterSituationId = query?.data?.pages[0].items[0]?.id;

  useBroadcastChannel<string>(BROADCAST_CHANNEL.CHARACTER_UPDATE, () => {
    queryClient.refetchQueries(QUERY_KEYS.CHARACTER.ONE(characterId));
  });

  const { postChannelMessage } = useBroadcastChannel<string>(
    BROADCAST_CHANNEL.CHARACTER_DELETE
  );

  const { data: situationInfo } = useQuery(
    QUERY_KEYS.SITUATION.ONE(characterSituationId as number),
    () => getAIChatSituationDetail(characterSituationId as number),
    {
      retry: false,
      enabled: !!characterSituationId,
    }
  );

  const chatRoomsQueries: ChatRoomsQueryType = {
    last_chat_in_days: 365,
    ordering: 'last_chat_newest',
    status: 'AT' as ChatRoomStatus,
  };

  if (characterId) {
    chatRoomsQueries.character_id = characterId;
  }

  const communityQueryKey = QUERY_KEYS.POST.CHARACTER_LIST(
    character?.id as number
  );

  const communityQuery = usePaginationQuery(
    communityQueryKey,
    ({ pageParam }) => fetchCharacterPosts(character?.id as number, pageParam),
    {
      enabled: !!character?.id,
    }
  );

  const communityCount = communityQuery.data?.pages[0].totalCount ?? 0;

  const [
    isChemiBottomModalOpen,
    { open: openChemiBottomModalOpen, close: closeChemiBottomModalOpen },
  ] = useModal('chemi-step-bottom-modal');

  const tabs = useTab<
    DefaultCharacterTab | OfficialCharacterTab | ChemingCharacterTab
  >(
    isCheming
      ? [
          { label: t('캐릭터'), value: 'character' },
          {
            label: t(`나의 페르소나`),
            value: 'profile',
          },
        ]
      : character?.type === 'normal'
      ? [
          { label: t('프로필'), value: 'profile' },
          {
            label: t(`후기 ${communityCount > 0 ? `(${communityCount})` : ''}`),
            value: 'community',
          },
        ]
      : [
          { label: t('상황'), value: 'situation' },
          {
            label: t(`후기 ${communityCount > 0 ? `(${communityCount})` : ''}`),
            value: 'community',
          },
        ],
    { replace: false }
  );
  const { activeTab, onChange: changeActiveTab } = tabs;

  const handleOnChangeTab = (
    tab: DefaultCharacterTab | OfficialCharacterTab | ChemingCharacterTab
  ) => {
    changeActiveTab(tab);

    if (
      (isCheming && tab === 'character') ||
      (isCheming && tab === 'profile')
    ) {
      firebase.logEvent('click_character_detail_tab', {
        tap_type: tab,
        character_id: characterId,
      });
    }
  };

  const isNewCharacter =
    (character?.released_at &&
      isBefore(
        new Date(),
        addDuration(new Date(character?.released_at), { days: 3 })
      )) ||
    false;

  const isMyCharacter = (me && me?.id === character?.player?.user_id) || false;

  const [
    isCharacterDeleteModalOpen,
    { open: openCharacterDeleteModal, close: closeCharacterDeleteModal },
  ] = useModal('character-delete');

  const { mutate: deleteCharacter } = useMutation(
    (characterId: number) => deletePlaySet(characterId),
    {
      onSettled: () => {
        closeCharacterDeleteModal();
        postChannelMessage('delete');

        setTimeout(() => {
          alert('캐릭터 삭제가 완료되었습니다.');
          if (platform === Platform.web) {
            router.back();
          }
          closePageSafe();
        }, 100);
      },
    }
  );

  const [actions] = useState<ActionSheetOption<CharacterDetailAction>[]>(
    isMyCharacter
      ? [
          { label: t('수정하기'), value: 'modify' },
          { label: t('삭제하기'), value: 'delete', isDanger: true },
        ]
      : [{ label: t('신고하기'), value: 'report', isDanger: true }]
  );

  const handleActionSheet = (value: CharacterDetailAction) => {
    switch (value) {
      case 'modify':
        const type = character?.can_have_visual ? 'visual' : 'default';
        if (isModal && !isCheming) {
          web.openPage(
            `/play/characters/${characterId}/edit?characterType=${type}`
          );
        } else {
          router.push(
            `/play/characters/${characterId}/edit?characterType=${type}`
          );
        }
        return;
      case 'delete':
        openCharacterDeleteModal();
        return;
      case 'report':
        if (isModal) {
          web.openPage(
            `/report${toQueryString({
              objectType: ReportObjectType.character,
              object_id: characterId,
            })}`
          );
        } else {
          router.push(
            `/report${toQueryString({
              objectType: ReportObjectType.character,
              object_id: characterId,
            })}`
          );
        }
        return;
    }
  };

  const handleConfirmDeleteCharacter = (characterId: number) => {
    deleteCharacter(characterId);
  };

  const handleSituationCardClick = (id: number) => {
    if (!id) {
      return;
    }
    setTimeout(() => {
      if (platform === Platform.web && id) {
        return router.push(`/play/situations/${id}/chat-room`);
      } else {
        return web.openPage(`/play/situations/${id}/chat-room`);
      }
    }, 300);

    firebase.logEvent('test_chatbot_character_detail_action', {
      target: 'situation',
      action_type: 'click',
    });
  };

  const handleStoryButtonClick = () => {
    firebase.logEvent('test_chatbot_character_detail_action', {
      target: 'story',
      action_type: 'click',
    });

    if (character?.story_id) {
      inflow(character?.story_id);
      if (isModal) {
        web.openPage(`/stories/${character?.story_id}`);
      } else {
        router.push(`/stories/${character?.story_id}`);
      }
    } else {
      alert('작품이 존재하지 않는 캐릭터입니다.');
    }
  };

  const handleModifyMyCharacter = () => {
    const type = character?.can_have_visual ? 'visual' : 'default';
    if (platform === Platform.web) {
      router.push(
        `/play/characters/${character?.id}/edit?characterType=${type}`
      );
    } else {
      web.openPage(
        `/play/characters/${character?.id}/edit?characterType=${type}`
      );
    }
  };

  const getPathOnly = (url: string) => url.split('?')[0].split('#')[0];

  const goToUrlOrRefresh = (url: string) => {
    const currentPath = getPathOnly(router.asPath);
    const targetPath = getPathOnly(url);

    if (currentPath === targetPath) {
      window.location.reload();
    } else {
      router.push(url);
    }
  };

  const handleChatButtonClick = async () => {
    setFlow({ name: '대화하기' });

    firebase.logEvent('test_chatbot_character_detail_action', {
      target: 'chat',
      action_type: 'click',
    });
    if (platform === Platform.web) {
      return goToUrlOrRefresh(
        `/play/situations/${characterSituationId}/chat-room`
      );
    } else {
      return web.openPage(`/play/situations/${characterSituationId}/chat-room`);
    }
  };

  const handleNavigateUser = (id: number) => {
    if (isModal) {
      web.openPage(`/users/${id}`);
    } else {
      router.push(`/users/${id}`);
    }
  };

  const handleClickTag = (id: number, name: string) => {
    if (platform === Platform.web) {
      router.push(`/play/characters/tags/${id}?title=${name}`);
    } else {
      web.openPage(`/play/characters/tags/${id}?title=${name}`);
    }
  };

  const handleNavigatePostCreate = async (e: SyntheticEvent) => {
    e.stopPropagation();
    const data = await fetchChatRooms({ character_id: characterId });
    const isCreatePostAvailable = data.items.length > 0 || isMyCharacter;

    if (isCreatePostAvailable) {
      if (platform === Platform.web) {
        router.push(
          `/play/characters/posts/create${toQueryString({
            characterId: characterId,
            playerId: character?.player?.user_id,
          })}`
        );
      }
      web.openPage(
        `/play/characters/posts/create${toQueryString({
          characterId: characterId,
          playerId: character?.player?.user_id,
        })}`
      );
    } else {
      showToast({
        text: t(
          '캐릭터와 대화를 나누면 글을 작성할 수 있어요!\n플레이 해보세요 ❤️'
        ),
        height: 100,
      });
    }
  };

  const handleClickStep = () => {
    openChemiBottomModalOpen();
  };

  useEffect(() => {
    setFlow({ name: '캐릭터상세' });
  }, []);

  useEffect(() => {
    firebase.logEvent('chatbot_character_inflow', {
      character_id: characterId,
      source: getFlow(),
    });
  }, [characterId, firebase]);

  useEffect(() => {
    if (shareUUID) {
      postUUID(shareUUID);
    }
  }, [shareUUID]);

  if (!character) {
    return <ErrorFallback status={404} />;
  }

  return (
    <Layout>
      <SafeArea bottom>
        <Header
          transparent={isModal || isCheming ? false : true}
          transparentFadeInAt={isCheming ? '0px' : '100px'}
          hideBorder={isCheming ? true : false}
          title={isCheming && '캐릭터 정보'}
          push={false}
          hideBackButton={isModal}
          right={(color) =>
            isMyCharacter && (
              <ActionSheet
                name="my-story-action"
                options={actions}
                onSelect={handleActionSheet}
              >
                {(open) => (
                  <button className="icon-btn" onClick={open}>
                    <Icon
                      icon={VerticalEllipsis}
                      color={color}
                      size={Sizes.medium}
                    />
                  </button>
                )}
              </ActionSheet>
            )
          }
        />

        {isCheming && situationInfo ? (
          <CharacterChemiInfoArea
            character={character}
            handleClickTag={handleClickTag}
            handleClickStep={handleClickStep}
            situationInfo={situationInfo}
          />
        ) : (
          <CharacterDetailInfoArea
            character={character}
            isNewCharacter={isNewCharacter}
            handleClickTag={handleClickTag}
            handleNavigateUser={handleNavigateUser}
            handleChatButtonClick={handleChatButtonClick}
          />
        )}

        <Tab {...tabs} fit onChange={handleOnChangeTab} />

        <Suspense fallback={<Loading push />}>
          <div>
            {activeTab === 'situation' && (
              <SituationList
                query={query}
                handleSituationCardClick={handleSituationCardClick}
              />
            )}
            {activeTab === 'community' && (
              <CharacterCommunityList
                query={communityQuery}
                character={character}
              />
            )}
            {isCheming && activeTab === 'character' && (
              <CharacterDetailInfo
                character={character}
                situationInfo={situationInfo}
                isMyCharacter={isMyCharacter}
              />
            )}
            {isCheming && activeTab === 'profile' && (
              <CharacterDetailInfo
                character={character.related_profile as IChatCharacter}
                situationInfo={situationInfo}
                isMyCharacter={isMyCharacter}
              />
            )}
            {!isCheming && activeTab === 'profile' && (
              <CharacterProfile character={character} />
            )}
          </div>
        </Suspense>
        <div css={floatingButtonContainerCSS}>
          <div className="floating-button-wrap">
            {!isMyCharacter && !!character?.story_id && (
              <Button
                className="btn"
                type={'border'}
                onClick={handleStoryButtonClick}
              >
                작품 볼래요
              </Button>
            )}
            {!isCheming && (
              <Button
                className="btn"
                onClick={handleNavigatePostCreate}
                type={'border'}
              >
                후기 쓰기
              </Button>
            )}
            {isMyCharacter && (
              <Button
                className="btn"
                type="border"
                onClick={handleModifyMyCharacter}
              >
                {'캐릭터 수정'}
              </Button>
            )}
            {isCheming && (
              <Button
                className="btn"
                type="fill"
                onClick={handleChatButtonClick}
              >
                {isModal ? '새로 대화하기 😉' : '우린 어떤 사이가 될까요? 🤫'}
              </Button>
            )}
          </div>
        </div>
        <ChemiStepBottomModal
          open={isChemiBottomModalOpen}
          onClose={closeChemiBottomModalOpen}
          chemiStory={situationInfo?.chemi_story}
        />
        <Push size={PushSize.floatingButton} />
        <Suspense fallback={<Loading center />}>
          <CharacterDeleteModal
            open={isCharacterDeleteModalOpen}
            onClose={closeCharacterDeleteModal}
            handleOnConfirm={handleConfirmDeleteCharacter}
            character={character}
          />
        </Suspense>
      </SafeArea>
    </Layout>
  );
};

/**
 * Page
 */

const CharacterDetailLoading = () => {
  return (
    <Layout>
      <CharacterDetailSkeleton />
    </Layout>
  );
};

const CharacterDetailPage: NextPage = () => {
  useScreen('캐릭터 상세');

  return (
    <Suspense fallback={CharacterDetailLoading()}>
      <CharacterDetail />
    </Suspense>
  );
};

export const getServerSideProps = withGetServerSideProps(
  async (ctx: GetServerSidePropsContext) => {
    const characterId = getRouterQuery(ctx.query.characterId) as number;
    const CHEMING_DOMAIN = process.env.NEXT_PUBLIC_CHEMING_DOMAIN;
    let hostname = '';

    if (ctx?.req) {
      hostname = ctx.req.headers.host ?? '';
    } else if (typeof window !== 'undefined') {
      hostname = window.location.host;
    }
    const chemingHost = CHEMING_DOMAIN ? new URL(CHEMING_DOMAIN).host : '';
    const isCheming = hostname === chemingHost;

    return await combineProps(
      ctx,
      withDehydrate(async () => {
        if (ctx.req.url?.startsWith('/_next/data')) {
          return undefined;
        }
        try {
          const { data } = await CHAT_BOT_API.get<
            IChatResponse<IChatCharacter>
          >(`/characters/${characterId}/`, {
            headers: {
              ['X-SERVICE-ID']: isCheming ? DOMAIN.cheming : DOMAIN.chatie,
            },
          });

          const characterInfo = data?.result;
          const domain = isCheming ? '케밍' : '채티';
          const title = `[${domain}] ${characterInfo?.name}과 대화하기`;
          return characterInfo?.profile_image
            ? {
                og: {
                  title: title,
                  image: {
                    url: characterInfo?.profile_image?.attachment?.url,
                    width:
                      characterInfo?.profile_image.attachment.width ?? 1200,
                    height:
                      characterInfo?.profile_image.attachment.height ?? 600,
                  },
                },
              }
            : undefined;
        } catch (err) {
          return undefined;
        }
      })
    );
  }
);

export default withAuth(CharacterDetailPage, {
  isSignInRequired: false,
  LoadingComponent: CharacterDetailLoading,
});
